import { useEffect, useState } from 'react';
import { useNavigate, useLocation, generatePath } from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { useAppDispatch } from '@store';
import { OrganizationType } from '../../types';
import {
  useLazyGetCustomerAgreementFileQuery,
  useLazyGetCustomerAgreementQuery,
} from '../../../customers/store/services/customers';
import { clearAgreementState } from '../../store/slices/documents/documents';
import { ApplicationType } from '../types';

import ROUTES from '../../../../routes';

const useAgreement = ({
  customerType,
  customerId,
  agreementId,
  isApplication,
  activePermissions,
}: {
  customerType: OrganizationType | ApplicationType;
  customerId?: string;
  agreementId?: string;
  isApplication?: boolean;
  activePermissions?: PermissionEnum[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [link, setLink] = useState('');

  const [getAgreement, { isLoading, isFetching }] =
    useLazyGetCustomerAgreementQuery();
  const [getFile] = useLazyGetCustomerAgreementFileQuery();

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const getAgreementFile = async (id: string) => {
    const response = await getFile(id);

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setLink(blob);
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);

    let backLink = '';

    if (isApplication) {
      if (customerType === ApplicationType.INDIVIDUAL) {
        backLink = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO;
      } else if (customerType === ApplicationType.CORPORATE) {
        backLink = ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO;
      } else if (customerType === ApplicationType.COMPANY_OFFICIAL) {
        backLink = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL;
      }
    } else {
      backLink = generatePath(
        customerType === OrganizationType.INDIVIDUAL
          ? ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS
          : ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS,
        { customerId: (customerId as string) || '' },
      );
    }

    setTimeout(() => {
      navigate(`${backLink}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      agreementId &&
      activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST)
    ) {
      getAgreement(agreementId);
      getAgreementFile(agreementId);
    }

    return () => {
      dispatch(clearAgreementState());
    };
  }, [agreementId, activePermissions]);

  return {
    isLoading,
    isFetching,
    link,
    panelIsOpen,
    handleClose,
  };
};

export default useAgreement;
